/**
 * Password policy (NIST SP 800-63B / OWASP ASVS V2.1): length over composition. No forced
 * character classes and no periodic expiry; instead common, predictable and context-derived
 * passwords are refused. The API is the authority; the apps show the same rules as hints.
 *
 * The impure pieces (breach check, AppError) live in apps/api/src/modules/auth/methods/password.ts.
 */
export const PASSWORD_RULES = { minLength: 10, maxLength: 128 } as const;

/** Frequent passwords that satisfy the length rule (shorter ones are already refused by length). */
const COMMON = new Set(
  [
    '1234567890', '12345678910', '123456789012', '0123456789', '1111111111', '0000000000', '1234512345',
    'qwertyuiop', 'qwertyuiop123', 'asdfghjkl1', 'zxcvbnm123', 'qwerty1234', 'qwerty12345', 'abcdefghij',
    'password12', 'password123', 'password1234', 'passw0rd123', 'contrasena', 'contrasena1', 'contrasena123',
    'contraseña', 'contraseña1', 'contraseña123', 'micontrasena', 'iloveyou12', 'teamo12345', 'tequiero123',
    'colombia123', 'colombia2024', 'colombia2025', 'colombia2026', 'medellin123', 'rionegro123', 'antioquia123',
    'bienvenido1', 'bienvenido123', 'welcome123', 'welcome1234', 'admin12345', 'administrador', 'administrador1',
    'administrator', 'superadmin1', 'despachador', 'despachador1', 'despachador123', 'conductor123', 'taxista123',
    'football123', 'baseball123', 'princesa123', 'dragon12345', 'monkey12345', 'sunshine123', 'letmein123',
    'abc1234567', 'abcd123456', 'a123456789', 'q1w2e3r4t5', '1q2w3e4r5t', '1qaz2wsx3edc', 'zaq12wsxcde',
  ].map((p) => p.toLowerCase()),
);

export interface PasswordContext {
  email?: string | null;
  fullName?: string | null;
  /** Company words (brand, slug): a password made of them is the first thing an attacker tries. */
  companyWords?: Array<string | null | undefined>;
}

const fold = (s: string) =>
  s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

function isSequence(s: string): boolean {
  if (s.length < 6) return false;
  const codes = [...s].map((c) => c.charCodeAt(0));
  const step = codes[1]! - codes[0]!;
  return Math.abs(step) <= 1 && codes.every((c, i) => i === 0 || c - codes[i - 1]! === step);
}

/** Words of the context that must not appear in the password (4+ letters to avoid false positives). */
function contextWords(ctx: PasswordContext): string[] {
  const words = [
    ...(ctx.email ? [ctx.email.split('@')[0]!, ...ctx.email.split('@')[0]!.split(/[._+-]/)] : []),
    ...(ctx.fullName ? ctx.fullName.split(/\s+/) : []),
    ...(ctx.companyWords ?? []).flatMap((w) => (w ? [w, ...w.split(/[\s._-]+/)] : [])),
  ];
  return [...new Set(words.map(fold).filter((w) => w.length >= 4))];
}

/** Problems of a candidate password, as sentences for the person. Empty = acceptable. */
export function passwordProblems(pw: string, ctx: PasswordContext = {}): string[] {
  const problems: string[] = [];
  const length = [...pw].length;
  if (length < PASSWORD_RULES.minLength) problems.push(`Debe tener al menos ${PASSWORD_RULES.minLength} caracteres.`);
  if (length > PASSWORD_RULES.maxLength) problems.push(`Debe tener como máximo ${PASSWORD_RULES.maxLength} caracteres.`);
  if (problems.length) return problems;
  const f = fold(pw);
  const compact = f.replace(/[\s._-]/g, '');
  if (COMMON.has(f) || COMMON.has(compact)) problems.push('Es una contraseña muy común. Elige otra.');
  else if (new Set(f).size <= 2 || isSequence(compact)) problems.push('Es demasiado predecible (caracteres repetidos o en secuencia).');
  else {
    // the remaining text after removing context words must still be a real secret
    const words = contextWords(ctx);
    let rest = compact;
    for (const w of words) rest = rest.split(w.replace(/[\s._-]/g, '')).join('');
    if (words.some((w) => compact.includes(w.replace(/[\s._-]/g, ''))) && rest.length < 6)
      problems.push('No uses tu nombre, tu correo ni el nombre de la empresa como contraseña.');
  }
  return problems;
}
